import { ClientsDataTable } from '@/components/(clients_table)/components/ClientsDataTable'
import { createClientsTableColumns } from '@/components/(clients_table)/components/clientsTableColumns'
import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useTRPC } from '@/trpc/react'
import { arktypeResolver } from '@hookform/resolvers/arktype'
import { COLORS } from '@repo/shared_utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type } from 'arktype'
import { PlusIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/clients/')({
  component: RouteComponent,
})

// Schema for client form validation
const clientFormSchema = type({
  name: 'string',
  location: 'string',
})

type ClientFormValues = typeof clientFormSchema.infer

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const { data: clients, isPending: isClientsPending } = useQuery({
    ...api.client.getClientsForBusiness.queryOptions(),
  })

  // Calculate overview metrics
  const totalClients = clients?.length ?? 0
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

  const recentClients =
    clients?.filter((client) => new Date(client.createdAt) >= thirtyDaysAgo).length ?? 0

  const uniqueLocations = new Set(
    clients
      ?.filter((client) => client.location && client.location.trim() !== '')
      .map((client) => client.location),
  ).size

  return (
    <DashboardShell>
      <DashboardHeader heading="Clients" text="Manage your organization's clients.">
        <AddClientDialog />
      </DashboardHeader>

      {/* Overview Stats */}
      {isClientsPending ? (
        <div className="grid gap-4 mb-6 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-2 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 mb-6 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Clients</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ color: COLORS[0] }}>
                {totalClients}
              </div>
              <div className="flex items-center mt-2">
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: COLORS[0],
                      width: '100%',
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Recent Clients (30 days)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ color: COLORS[1] }}>
                {recentClients}
              </div>
              <div className="flex items-center mt-2">
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: COLORS[1],
                      width: totalClients > 0 ? `${(recentClients / totalClients) * 100}%` : '0%',
                    }}
                  />
                </div>
                <span className="text-sm text-muted-foreground ml-2">
                  {totalClients > 0 ? Math.round((recentClients / totalClients) * 100) : 0}%
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Unique Locations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ color: COLORS[2] }}>
                {uniqueLocations}
              </div>
              <div className="flex items-center mt-2">
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: COLORS[2],
                      width:
                        totalClients > 0 ? `${(uniqueLocations / totalClients) * 100}%` : '0%',
                    }}
                  />
                </div>
                <span className="text-sm text-muted-foreground ml-2">
                  {totalClients > 0 ? Math.round((uniqueLocations / totalClients) * 100) : 0}%
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="space-y-4">
        {isClientsPending ? (
          <div className="rounded-md border p-4">
            <div className="text-center text-muted-foreground">Loading clients...</div>
          </div>
        ) : (
          <ClientsDataTable
            columns={createClientsTableColumns({
              clientDetailRoute: '/dashboard/admin/clients/$clientId',
            })}
            data={clients ?? []}
          />
        )}
      </div>
    </DashboardShell>
  )
}

function AddClientDialog() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const form = useForm<ClientFormValues>({
    resolver: arktypeResolver(clientFormSchema),
    defaultValues: {
      name: '',
      location: '',
    },
  })

  const createClientMutation = useMutation({
    ...api.client.createClientForBusiness.mutationOptions(),
  })

  async function onSubmit(data: ClientFormValues) {
    createClientMutation.mutate(
      {
        name: data.name,
        location: data.location,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.client.getClientsForBusiness.queryKey(),
          })
          toast.success('Client created successfully')
        },
        onError: (error) => {
          console.error(error)
          toast.error('Failed to create client', {
            action: {
              label: 'Close',
              onClick: () => toast.dismiss(),
            },
          })
        },
      },
    )
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="cursor-pointer">
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Client
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Client</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter client name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter client location" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={createClientMutation.isPending}>
              {createClientMutation.isPending ? 'Creating...' : 'Add Client'}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
